import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Package, Stack, Truck, Cube, FloppyDisk, X, ArrowRight, ArrowLeft, GraduationCap, CheckCircle } from "@phosphor-icons/react";

const STEPS = [
  { title: "PalletNext'e Hoş Geldiniz", text: "İlk sevkiyat planınızı gerçek ekranları kullanarak birlikte oluşturalım. Tur, işlem yapmanız gereken alanları gösterecek; uygulama kullanıma açık kalacak.", path: "/app", icon: GraduationCap },
  { title: "1. Ürünlerini Ekle", text: "Bu ekran gerçek Ürün Yönetimi ekranıdır. Yeni ürün ekleyebilir veya mevcut ürünlerin ölçü, ağırlık ve yerleşim kurallarını kontrol edebilirsiniz. Ürünleriniz hazırsa sonraki adıma geçin.", path: "/products", icon: Package, live: true, target: "[data-testid='add-product']", nextLabel: "Ürünler Hazır · Devam" },
  { title: "2. Yükleme Tipini Seç", text: "Palet, konteyner, tır veya kamyon seçeneklerini burada gerçek haliyle inceleyin. Gerekirse özel yükleme tipi tanımlayın. Kullanacağınız yükleme tipi hazırsa Yeni Plan ekranına geçin.", path: "/pallet-types", icon: Stack, live: true, nextLabel: "Yükleme Tipi Hazır · Devam" },
  { title: "3. Sevkiyat ve Miktarları Gir", text: "Şimdi gerçek Yeni Plan formunu doldurun. Sevkiyat bilgilerini kontrol edin, ürün kategorisini seçin ve en az bir SKU için koli miktarı girin. Optimize Et düğmesi aktif olduğunda tur otomatik olarak sonraki adıma geçer.", path: "/plans/new", icon: Truck, live: true, target: "[data-testid='plan-category-filter']", autoWait: "quantity" },
  { title: "4. Optimize Et", text: "Form hazır. Sayfadaki gerçek mavi Optimize Et düğmesine basın. Tur, başarılı H3POE sonucunu algılayıp otomatik ilerleyecek.", path: "/plans/new", icon: Cube, live: true, target: "[data-testid='optimize-btn']", autoWait: "optimize" },
  { title: "5. Planı Kaydet ve Aç", text: "Gerçek optimizasyon sonucu hazır. Yükleme birimlerini kontrol edin ve sayfadaki Planı Kaydet ve Aç düğmesine basın. Kayıt başarılı olduğunda gerçek plan detayına geçeceğiz.", path: "/plans/new", icon: FloppyDisk, live: true, target: "[data-testid='save-plan-btn']", autoWait: "save" },
  { title: "6. 3D Sonucu İncele", text: "Plan kaydedildi. Şimdi gerçek 3D yerleşim ekranındasınız. Yükleme birimleri arasında gezebilir ve gerekiyorsa manuel düzenleme araçlarını kullanabilirsiniz.", path: null, icon: CheckCircle, live: true },
];

const QUANTITY_STEP = 3;
const OPTIMIZE_STEP = 4;
const SAVE_STEP = 5;
const RESULT_STEP = 6;

export default function ProductTour({ user, forceOpen = false, onClose }) {
  const nav = useNavigate();
  const location = useLocation();
  const key = useMemo(() => `palletnext_tour_v012_${user?.id || "guest"}`, [user?.id]);
  const progressKey = useMemo(() => `palletnext_tour_progress_v012_${user?.id || "guest"}`, [user?.id]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(() => {
    const stored = Number(sessionStorage.getItem(progressKey));
    return Number.isInteger(stored) && stored >= 0 && stored < STEPS.length ? stored : 0;
  });

  const setProgress = (nextStep) => {
    sessionStorage.setItem(progressKey, String(nextStep));
    setStep(nextStep);
  };

  useEffect(() => {
    if (!user?.id) return;
    if (forceOpen || localStorage.getItem(key) !== "done") {
      const timer = window.setTimeout(() => setOpen(true), 450);
      return () => window.clearTimeout(timer);
    }
  }, [user?.id, key, forceOpen]);

  useEffect(() => {
    const stored = Number(sessionStorage.getItem(progressKey));
    setStep(Number.isInteger(stored) && stored >= 0 && stored < STEPS.length ? stored : 0);
  }, [progressKey]);

  // A live step must always be displayed on the screen it teaches.
  useEffect(() => {
    if (!open) return;
    const expectedPath = STEPS[step]?.path;
    if (expectedPath && location.pathname !== expectedPath) {
      nav(expectedPath);
    }
  }, [open, step, location.pathname, nav]);

  // Quantity step: do not let the user click through it. When a real quantity is entered,
  // NewPlan enables the real Optimize button and the tour advances automatically.
  useEffect(() => {
    if (!open || step !== QUANTITY_STEP || location.pathname !== "/plans/new") return undefined;
    const advanceWhenReady = () => {
      const optimizeButton = document.querySelector("[data-testid='optimize-btn']");
      if (optimizeButton && !optimizeButton.disabled) setProgress(OPTIMIZE_STEP);
    };
    advanceWhenReady();
    const observer = new MutationObserver(advanceWhenReady);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
    return () => observer.disconnect();
  }, [open, step, location.pathname]);

  // Optimize step: a successful real optimization renders the real Save button.
  useEffect(() => {
    if (!open || step !== OPTIMIZE_STEP || location.pathname !== "/plans/new") return undefined;
    const advanceWhenResultExists = () => {
      if (document.querySelector("[data-testid='save-plan-btn']")) setProgress(SAVE_STEP);
    };
    advanceWhenResultExists();
    const observer = new MutationObserver(advanceWhenResultExists);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [open, step, location.pathname]);

  // Save step: only a real successful save/navigation may unlock the 3D result step.
  useEffect(() => {
    if (!open || step !== SAVE_STEP) return;
    const isPlanDetail = /^\/plans\/[^/]+$/.test(location.pathname) && location.pathname !== "/plans/new";
    if (isPlanDetail) setProgress(RESULT_STEP);
  }, [open, step, location.pathname]);

  // Highlight the real control without putting a click-blocking overlay over the application.
  useEffect(() => {
    if (!open || !STEPS[step]?.target) return undefined;
    let target = null;
    let observer = null;
    const clearTarget = () => {
      if (!target) return;
      target.style.boxShadow = target.dataset.tourOldShadow || "";
      target.style.position = target.dataset.tourOldPosition || "";
      target.style.zIndex = target.dataset.tourOldZIndex || "";
      delete target.dataset.tourOldShadow;
      delete target.dataset.tourOldPosition;
      delete target.dataset.tourOldZIndex;
      target = null;
    };
    const highlight = () => {
      const nextTarget = document.querySelector(STEPS[step].target);
      if (!nextTarget || nextTarget === target) return;
      clearTarget();
      target = nextTarget;
      target.dataset.tourOldShadow = target.style.boxShadow || "";
      target.dataset.tourOldPosition = target.style.position || "";
      target.dataset.tourOldZIndex = target.style.zIndex || "";
      target.style.position = "relative";
      target.style.zIndex = "290";
      target.style.boxShadow = "0 0 0 4px rgba(37, 99, 235, 0.30), 0 0 0 8px rgba(255,255,255,0.92)";
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    highlight();
    observer = new MutationObserver(highlight);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer?.disconnect();
      clearTarget();
    };
  }, [open, step, location.pathname]);

  const finish = () => {
    localStorage.setItem(key, "done");
    sessionStorage.removeItem(progressKey);
    setOpen(false);
    onClose?.();
  };
  const close = () => finish();
  const current = STEPS[step];
  const Icon = current.icon;

  const goForward = () => {
    if (step >= QUANTITY_STEP) return;
    const nextStep = Math.min(STEPS.length - 1, step + 1);
    setProgress(nextStep);
    const nextPath = STEPS[nextStep].path;
    if (nextPath && location.pathname !== nextPath) nav(nextPath);
  };

  const goBack = () => {
    const previousStep = Math.max(0, step - 1);
    setProgress(previousStep);
    const previousPath = STEPS[previousStep].path;
    if (previousPath && location.pathname !== previousPath) nav(previousPath);
  };

  if (!open) return null;

  if (current.live) {
    const status = step === QUANTITY_STEP
      ? "Canlı form açık · gerçek koli miktarı girmeniz bekleniyor"
      : step === OPTIMIZE_STEP
        ? "Gerçek H3POE optimizasyonu bekleniyor"
        : step === SAVE_STEP
          ? "H3POE sonucu hazır · gerçek kayıt bekleniyor"
          : step === RESULT_STEP
            ? "Plan kaydedildi · gerçek 3D ekranındasınız"
            : "Canlı ekran açık · aşağıdaki uygulamayı kullanabilirsiniz";

    return (
      <div className="fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-[300] w-[calc(100%-2rem)] sm:w-[420px] bg-white border border-slate-200 shadow-2xl rounded-lg overflow-hidden" data-testid="product-tour" data-tour-mode="live-coach">
        <div className="bg-[#0F172A] text-white px-5 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-blue-300 font-bold">PalletNext Canlı Başlangıç Turu</div>
            <div className="text-lg font-extrabold mt-1">{current.title}</div>
          </div>
          <button onClick={close} className="p-2 hover:bg-white/10 rounded" aria-label="Turu kapat"><X size={18}/></button>
        </div>
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 bg-blue-50 border border-blue-200 text-blue-700 rounded-md flex items-center justify-center"><Icon size={22} weight="duotone"/></div>
            <div>
              <p className="text-sm text-slate-600 leading-6 m-0">{current.text}</p>
              <div className="mt-3 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-3 py-2">{status}</div>
            </div>
          </div>
          <div className="mt-5 flex gap-1.5">{STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-blue-600" : "bg-slate-200"}`}/>)}</div>
          <div className="mt-2 text-xs text-slate-400">Adım {step + 1} / {STEPS.length}</div>
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <button onClick={finish} className="text-sm text-slate-500 hover:text-slate-900">Turu Geç</button>
          <div className="flex gap-2">
            {step > 0 && step <= QUANTITY_STEP && <button onClick={goBack} className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white text-sm font-semibold"><ArrowLeft size={15}/>Geri</button>}
            {step > 0 && step < QUANTITY_STEP && <button onClick={goForward} className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">{current.nextLabel || "Devam"}<ArrowRight size={15}/></button>}
            {step === RESULT_STEP && <button onClick={finish} className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">Turu Tamamla<CheckCircle size={16} weight="bold"/></button>}
          </div>
        </div>
      </div>
    );
  }

  // Only the welcome screen is modal. After this, every step becomes live and non-blocking.
  return <div className="fixed inset-0 z-[300] bg-slate-950/60 backdrop-blur-[2px] flex items-center justify-center p-4" data-testid="product-tour" data-tour-mode="welcome">
    <div className="w-full max-w-xl bg-white border border-slate-200 shadow-2xl rounded-lg overflow-hidden">
      <div className="bg-[#0F172A] text-white px-5 sm:px-7 py-5 flex items-start justify-between gap-4">
        <div><div className="text-[10px] uppercase tracking-[0.22em] text-blue-300 font-bold">PalletNext Başlangıç Turu</div><div className="text-lg sm:text-xl font-extrabold mt-1">{current.title}</div></div>
        <button onClick={close} className="p-2 hover:bg-white/10 rounded" aria-label="Turu kapat"><X size={18}/></button>
      </div>
      <div className="p-5 sm:p-7">
        <div className="flex items-start gap-4"><div className="shrink-0 w-11 h-11 bg-blue-50 border border-blue-200 text-blue-700 rounded-md flex items-center justify-center"><Icon size={24} weight="duotone"/></div><p className="text-sm sm:text-base text-slate-600 leading-7 m-0">{current.text}</p></div>
        <div className="mt-6 flex gap-1.5">{STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-blue-600" : "bg-slate-200"}`}/>)}</div>
        <div className="mt-3 text-xs text-slate-400">Adım {step + 1} / {STEPS.length}</div>
      </div>
      <div className="px-5 sm:px-7 py-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <button onClick={finish} className="text-sm text-slate-500 hover:text-slate-900">Turu Geç</button>
        <button onClick={goForward} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
          Canlı Turu Başlat<ArrowRight size={15}/>
        </button>
      </div>
    </div>
  </div>;
}
